import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useAuthStore } from '@/store/useAuthStore';
import { NavigationUtils } from '@/utils/userNavigation';

/**
 * Custom hook for user navigation that automatically stays in sync with auth state
 * Provides reactive navigation functions that update when user/auth state changes
 */
export const useUserNavigation = () => {
  const router = useRouter();
  const { user, isAuthenticated } = useAuthStore();

  // Memoized navigation URLs that update when user changes
  const navigationUrls = useMemo(() => ({
    homeDirectory: NavigationUtils.getUserHomeDirectory(user),
    postLoginRedirect: NavigationUtils.getPostLoginRedirect(user),
    postSignupRedirect: NavigationUtils.getPostSignupRedirect(user),
    goHomeUrl: NavigationUtils.getGoHomeUrl(user),
  }), [user]);

  // Navigation functions
  const goHome = useCallback(() => {
    router.push(navigationUrls.homeDirectory);
  }, [router, navigationUrls.homeDirectory]);

  const redirectAfterLogin = useCallback(() => {
    // Only redirect when both authenticated AND user data is available
    if (isAuthenticated && user) {
      router.push(navigationUrls.postLoginRedirect);
    }
  }, [router, isAuthenticated, user, navigationUrls.postLoginRedirect]);

  const redirectAfterSignup = useCallback(() => {
    if (user) {
      router.push(navigationUrls.postSignupRedirect);
    }
  }, [router, user, navigationUrls.postSignupRedirect]);

  // Navigation state
  const canNavigateHome = useMemo(() => {
    return isAuthenticated && !!user;
  }, [isAuthenticated, user]);

  return {
    // URLs (reactive to user changes)
    homeDirectory: navigationUrls.homeDirectory,
    postLoginRedirect: navigationUrls.postLoginRedirect,
    postSignupRedirect: navigationUrls.postSignupRedirect,
    goHomeUrl: navigationUrls.goHomeUrl,
    
    // Navigation functions
    goHome,
    redirectAfterLogin,
    redirectAfterSignup,
    
    // State
    canNavigateHome,
    user,
    isAuthenticated,
  };
};

export default useUserNavigation;
